import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font
} from "@react-pdf/renderer";
import roboto from '../../fonts/Roboto-Regular.ttf';
import robotoBold from '../../fonts/Roboto-Bold.ttf';
import checked from '../../images/checkbox-checked.png';
import unchecked from '../../images/checkbox-unchecked.png';
import paperclip from '../../images/paperclip.png';
import moment from 'moment';
import { SUBMISSION_STATUS } from '../form/constants';

Font.register({ family: 'Roboto', format: 'truetype', src: roboto });
Font.register({ family: 'Roboto-Bold', format: 'truetype', src: robotoBold });

export default function PdfDocument ({ data, files, isInternal }) {
  const showClassification = data.status === SUBMISSION_STATUS.PENDING_DISTRIBUTION || data.status === SUBMISSION_STATUS.COMPLETE;

  return (
    <Document>
      <Page style={styles.page} wrap={false}>
        {/* Header */}
        {showClassification && 
          <View fixed style={styles.header}>
            <Text style={styles.classificationText}>
              {data?.classification.toUpperCase()}
            </Text>
          </View>
        }
        <Text style={styles.title}>
          Request For Information
        </Text>
          {PdfSections.map((section, i) => (
            <Section section={section} data={data} isInternal={isInternal} key={i}/>
          ))}
          {/* Attachments */}
          <>
            <Text style={styles.attachmentHeader}>Attachments</Text>
            <View>
              {files.map((file, i) => <Attachment name={file} key={i}/>)}
            </View>
          </>
        {/* Footer with classification & page numbers */}
        {showClassification &&
          <Text style={[styles.classificationFooter, styles.classificationText]} fixed>
            {data?.classification.toUpperCase()}
          </Text>
        }
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
}

const Section = ({ section, data, isInternal }) => {
  // There are some sections that are all PNNL Only
  // If this is an external site, don't render sections that are entirely PNNL Only
  if (!isInternal) {
    const internalOnlyReducer = (acc, curr) => acc && curr;
    const allInternal = section.map(x => x.internalOnly || false)
                                .reduce(internalOnlyReducer, true);
    if(allInternal)
    {
      return null;
    }
  }
  
  return (
      
      <View style={styles.section}>
        {section.map((field, i) => {

          {/* don't display internal only fields to the external site */}
          if (field.internalOnly && !isInternal) {
            return null;
          }

          {/* Format date fields or empty fields */}
          let value = data[field.key] || " ";
          if (field.date && !isNaN(Date.parse(value))) {
            value = moment(value).format("MM/DD/YYYY");
          }

          {/* Render checkbox fields */}
          if (field.checkbox) {
            const isChecked = data[field.key] 
            return (
              <View style={styles.checkField} key={i}>
                <Text style={styles.label}>{field.label}:</Text>
                <Image src={isChecked ? checked : unchecked} style={styles.checkbox}/>
              </View>
            )
          }

          {/* Render basic text fields or text area fields */}
          return (
            <View style={field.textArea ? styles.textAreaField : styles.field} key={i}>
              <Text style={styles.label}>{field.label}:</Text>
              <Text style={styles.value}>{value}</Text>
            </View>
          )
        })
        }
      </View>
  )
}

const Attachment = ({ name }) => {
  return (
    <Text style={styles.attachment}>
      <Image src={paperclip} style={styles.attachmentIcon}/>
      <Text>{name}</Text>
    </Text>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: '20 15 60 15',
    fontFamily: "Roboto",
  },
  header: {
    width: "100%",
    marginBottom: 0,
    textAlign: 'center',
  },
  title: {
    textAlign: 'center',
    fontFamily: 'Roboto-Bold',
    paddingBottom: 15,
  },
  section: {
    border: '1px solid black',
    padding: '10px',
    borderRadius: '3px',
    fontSize: 10,
    marginBottom: 5,
  },
  field: {
    paddingBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    paddingRight: 30
  },
  checkField: {
    paddingBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  textAreaField: {
    paddingBottom: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontFamily: 'Roboto-Bold',
    marginRight: 10
  },
  value: {
    paddingLeft: 10,
    marginRight: 40
  },
  checkbox: {
    width: 12,
    height: 12,
    marginLeft: 10,
  },
  classificationFooter: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
  classificationText: {
    opacity: 0.6,
    fontSize: 12
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 15,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  attachmentHeader: {
    fontSize: 10,
    fontFamily: 'Roboto-Bold',
  },
  attachment: {
    fontSize: 10,
    fontFamily: 'Roboto',
  },
  attachmentIcon: {
    width: 10,
    height: 10,
  }
});

const PdfSections =[
  [
    {
      key: 'contractNumber',
      label: 'Contract Number',
    },
    {
      key: 'taskOrderNumber',
      label: 'Task Order Number',
    },
    {
      key: 'projectName',
      label: 'Project Name',
    },
    {
      key: 'projectLocation',
      label: 'Project Location',
    },
    {
      key: 'contractor',
      label: 'Contractor',
    },
    {
      key: 'rfiNumber',
      label: 'RFI Number',
    },
    {
      key: 'submissionDate',
      label: 'Date',
      date: true,
    },
    {
      key: 'subject',
      label: 'Subject',
    },
    {
      key: 'srNumber',
      label: 'SR Number',
      internalOnly: true
    },
    {
      key: 'submittersName',
      label: "Submitter's Name",
    },
    {
      key: 'submittersEmail',
      label: "Submitter's Email",
    },
    {
      key: 'submittersPhone',
      label: "Submitter's Phone",
    },
  ],
  [
    {
      key: 'challengeLocation',
      label: 'Location of Challenge',
    },
    {
      key: 'pages',
      label: 'Pages',
    },
    {
      key: 'references',
      label: 'References',
    },
  ],
  [
    {
      key: 'informationRequested',
      label: 'Problem/Information Requested',
      textArea: true
    },
    {
      key: 'requestedBy',
      label: 'Information Requested By',
    },
    {
      key: 'replyNeededDate',
      label: 'Date Reply Needed',
      date: true,
    },
  ],
  [
    {
      key: 'proposedResolution',
      label: "Contractor's Interpretation and Proposed Resolution",
      textArea: true
    },
    {
      key: 'scheduleImpact',
      label: 'Potential Schedule Impact',
      checkbox: true
    },
    {
      key: 'scheduleDescription',
      label: 'Describe Impact',
    },
    {
      key: 'costImpact',
      label: 'Potential Cost Impact',
      checkbox: true
    },
    {
      key: 'costDescription',
      label: 'Describe Impact',
    },
  ],
  [
    {
      key: 'response',
      label: 'Response',
      internalOnly: true,
      textArea: true
    },
    {
      key: 'isClarificationOnly',
      label: 'Clarification Only',
      internalOnly: true,
      checkbox: true
    },
    {
      key: 'isCnRequired',
      label: 'CN Required',
      internalOnly: true,
      checkbox: true
    },
    {
      key: 'isAttached',
      label: 'Attached',
      internalOnly: true,
      checkbox: true
    },
  ],
  [
    {
      key: 'responseBy',
      label: 'Response By',
      internalOnly: true,
    },
    {
      key: 'responseDate',
      label: 'Date',
      date: true,
      internalOnly: true,
    },
    {
      key: 'jobTitle',
      label: 'Title',
      internalOnly: true,
    },
  ]
]